import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { carryDelta } from "./stackCarry.js";
import type { CSSProperties, PointerEvent } from "react";
import { getCard } from "../../content/lookups.js";
import { innRequiredCount } from "../../domain/reducer/resolveInn.js";
import { canPlayCard } from "../../domain/selectors/canPlayCard.js";
import type { CardDefinition, CardInstance, Command, PendingChoice, RunState } from "../../domain/types/index.js";
import { CardView } from "../../components/CardView.js";
import { StatIcon } from "../../components/StatIcon.js";
import { isEligibleHandSelection, isHandSelection, selectedHandIds } from "./selectionPresentation.js";

type HandListProps = {
  run: RunState;
  pending: PendingChoice | null;
  dispatch: (action: Command) => void;
  /** 山札から配る演出をかけるカードID（手札内の並び順）。 */
  dealtIds?: readonly string[];
  /** 配り始めるまでの遅延（リシャッフル演出の完了を待つためなど）。 */
  dealBaseDelayMs?: number;
};

type DragMoveEvent = Pick<PointerEvent<HTMLButtonElement>, "pointerId" | "clientX" | "clientY"> & {
  preventDefault: () => void;
};

type DragEndEvent = Pick<PointerEvent<HTMLButtonElement>, "pointerId">;

const DRAG_CARD_WIDTH = 118;
const DRAG_CARD_HEIGHT = 157;
const DRAG_CARD_GAP = 16;
const HAND_CARD_WIDTH = 108;
const MIN_FAN_STEP = 14;

function overlapsDropZone(x: number, y: number, zone: DOMRect): boolean {
  const card = {
    left: x - DRAG_CARD_WIDTH / 2,
    right: x + DRAG_CARD_WIDTH / 2,
    top: y - DRAG_CARD_GAP - DRAG_CARD_HEIGHT,
    bottom: y - DRAG_CARD_GAP,
  };
  return card.left < zone.right && card.right > zone.left && card.top < zone.bottom && card.bottom > zone.top;
}

function fanAngle(index: number, count: number, focusedIndex: number | null = null): number {
  if (count < 2) return 0;
  const roundAngle = (angle: number) => Math.round(angle * 10) / 10;
  const outerAngle = count <= 6 ? 18 : Math.max(9, 18 - (count - 6) * 3);
  const defaultAngle = ((index / (count - 1)) * 2 - 1) * outerAngle;
  if (focusedIndex !== null) {
    const focusedDefaultAngle = ((focusedIndex / (count - 1)) * 2 - 1) * outerAngle;
    const centerGap = outerAngle * 2 / 3;
    const outerBoost = Math.min(4, outerAngle / 3);
    // 手札は右のカードが常に前面に重なる。右端以外は少し左へ倒して、
    // 右側に重なったカードから対象の面を見えやすくする。
    const focusedAngle = focusedIndex === count - 1
      ? focusedDefaultAngle
      : Math.max(-(outerAngle + outerBoost), Math.min(-4, focusedDefaultAngle - 6));
    if (index === focusedIndex) return focusedAngle;
    if (index < focusedIndex) {
      const leftCount = focusedIndex;
      const progress = leftCount === 1 ? 1 : index / (leftCount - 1);
      const leftOuter = -(outerAngle + outerBoost);
      return roundAngle(leftOuter + (focusedAngle - centerGap - leftOuter) * progress);
    }
    const rightCount = count - focusedIndex - 1;
    const progress = rightCount === 1 ? 1 : (index - focusedIndex - 1) / (rightCount - 1);
    const rightOuter = outerAngle + outerBoost;
    return roundAngle(focusedAngle + centerGap + (rightOuter - focusedAngle - centerGap) * progress);
  }
  return defaultAngle;
}

function fanSteps(count: number): number[] {
  if (count < 2) return [HAND_CARD_WIDTH];
  // 通常時は従来の扇の密度を保つ。カードが増えたときだけ段階的に詰める。
  const base = count <= 6 ? 36 : Math.max(MIN_FAN_STEP, 36 - (count - 6) * 5);
  return Array.from({ length: count }, (_, index) => index === 0 ? HAND_CARD_WIDTH : base);
}

function fanFocusOffset(index: number, focusedIndex: number | null): number {
  if (focusedIndex === null || index <= focusedIndex) return 0;
  // 右のカードが上に重なるため、対象より右側を段階的に外へ逃がす。
  return Math.min(44, (index - focusedIndex) * 18);
}

type HandCardProps = {
  card: CardInstance;
  run: RunState;
  pending: Extract<PendingChoice, { kind: "cellar" | "inn" | "handDisruption" | "meeting" | "sendoff" | "workerCycle" | "stables" | "training" | "caseExclude" | "psychologicalCounseling" | "specialTaskTeam" }> | null;
  dispatch: (action: Command) => void;
  onPointerDown: (event: PointerEvent<HTMLButtonElement>, card: CardInstance, definition: CardDefinition, playable: boolean, index: number) => void;
  onPointerMove: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLButtonElement>) => void;
  onLostPointerCapture: (event: PointerEvent<HTMLButtonElement>) => void;
  onPreview: (event: PointerEvent<HTMLButtonElement>, card: CardInstance, definition: CardDefinition) => void;
  onPreviewEnd: () => void;
  onLongPressStart: (event: PointerEvent<HTMLButtonElement>, card: CardInstance, definition: CardDefinition) => void;
  onLongPressMove: (event: PointerEvent<HTMLButtonElement>) => void;
  onLongPressEnd: (event: PointerEvent<HTMLButtonElement>) => void;
  fanAngle: number;
  fanStep: number;
  fanOffset: number;
  index: number;
  /** 山札から配られる演出の stagger 順。null なら演出なし。 */
  dealOrder: number | null;
  dealBaseDelayMs: number;
};

function HandCard({ card, run, pending, dispatch, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onLostPointerCapture, onPreview, onPreviewEnd, onLongPressStart, onLongPressMove, onLongPressEnd, fanAngle, fanStep, fanOffset, index, dealOrder, dealBaseDelayMs }: HandCardProps) {
  const def = getCard(card.cardId);
  if (!def) return null;
  const dealClass = dealOrder !== null ? " is-dealt" : "";
  const dealDelay = dealOrder !== null ? { animationDelay: `${dealBaseDelayMs + dealOrder * 70}ms` } : undefined;
  if (pending) {
    const eligible = isEligibleHandSelection(pending, def, Boolean(card.temporaryForCase));
    const picked = selectedHandIds(pending).includes(card.instanceId);
    return (
      <CardView
        card={card}
        definition={def}
        playable={eligible}
        inactive={!eligible}
        selectable
        picked={picked}
        onClick={eligible ? () => dispatch({ type: "TOGGLE_SELECTION", instanceId: card.instanceId }) : undefined}
        onPointerDown={(event) => {
          onPreviewEnd();
        }}
        onPointerEnter={(event) => onPreview(event, card, def)}
        onPointerLeave={onPreviewEnd}
        style={{ "--fan-angle": `${fanAngle}deg`, "--fan-step": `${fanStep}px`, "--fan-focus-offset": `${fanOffset}px`, ...dealDelay } as CSSProperties}
        className={dealClass || undefined}
      />
    );
  }
  const playable = canPlayCard(run, def);
  return (
    <CardView
      card={card}
      definition={def}
      playable={playable}
      inactive={!playable}
      className={`${playable ? "is-draggable" : ""}${dealClass}`}
      onPointerDown={(event) => {
        onPreviewEnd();
        onLongPressStart(event, card, def);
        onPointerDown(event, card, def, playable, index);
      }}
      onPointerMove={(event) => {
        onLongPressMove(event);
        onPointerMove(event);
      }}
      onPointerUp={(event) => {
        onLongPressEnd(event);
        onPointerUp(event);
      }}
      onPointerCancel={(event) => {
        onLongPressEnd(event);
        onPointerCancel(event);
      }}
      onLostPointerCapture={onLostPointerCapture}
      onPointerEnter={(event) => onPreview(event, card, def)}
      onPointerLeave={onPreviewEnd}
      style={{ "--fan-angle": `${fanAngle}deg`, "--fan-step": `${fanStep}px`, "--fan-focus-offset": `${fanOffset}px`, ...dealDelay } as CSSProperties}
    />
  );
}

export function HandList({ run, pending, dispatch, dealtIds = [], dealBaseDelayMs = 0 }: HandListProps) {
  const selecting = isHandSelection(pending);
  const handRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<{ card: CardInstance; definition: CardDefinition; pinned: boolean } | null>(null);
  const [dragPreview, setDragPreview] = useState<{ card: CardInstance; definition: CardDefinition; x: number; y: number } | null>(null);
  const [focusedCardIndex, setFocusedCardIndex] = useState<number | null>(null);
  const drag = useRef<{
    pointerId: number;
    instanceId: string;
    cardInstance: CardInstance;
    definition: CardDefinition;
    card: HTMLButtonElement;
    startX: number;
    startY: number;
    dragging: boolean;
    dropZone: HTMLElement | null;
  } | null>(null);
  const longPress = useRef<{ pointerId: number; startX: number; startY: number; timer: number; card: CardInstance; definition: CardDefinition } | null>(null);

  const dismissPreview = () => setPreview(null);

  const cancelLongPress = () => {
    if (!longPress.current) return;
    window.clearTimeout(longPress.current.timer);
    longPress.current = null;
  };

  const clearDrag = () => {
    const active = drag.current;
    if (!active) return;
    active.card.classList.remove("is-dragging");
    active.dropZone?.classList.remove("is-card-drop-target");
    document.querySelectorAll<HTMLElement>("[data-card-dropzone='play']").forEach((zone) => zone.classList.remove("is-card-drop-ready"));
    setDragPreview(null);
    drag.current = null;
    setFocusedCardIndex(null);
  };

  useEffect(() => () => {
    clearDrag();
    cancelLongPress();
  }, []);

  useEffect(() => {
    const cancelDragOnInterruption = () => clearDrag();
    window.addEventListener("blur", cancelDragOnInterruption);
    document.addEventListener("visibilitychange", cancelDragOnInterruption);
    return () => {
      window.removeEventListener("blur", cancelDragOnInterruption);
      document.removeEventListener("visibilitychange", cancelDragOnInterruption);
    };
  }, []);

  const visibleCards = run.hand;
  const steps = fanSteps(visibleCards.length);

  // 配られるカードの発生源（山札ゾーン中心）への移動量を実測してCSS変数に渡す。
  useLayoutEffect(() => {
    const handEl = handRef.current;
    const deckZone = document.querySelector('[data-deck-zone="deck"]');
    if (!handEl || !deckZone || dealtIds.length === 0) return;
    const deckRect = deckZone.getBoundingClientRect();
    for (const id of dealtIds) {
      const el = handEl.querySelector<HTMLElement>(`[data-instance-id="${id}"]`);
      if (!el) continue;
      const { x, y } = carryDelta(el.getBoundingClientRect(), deckRect);
      el.style.setProperty("--deal-x", `${Math.round(x)}px`);
      el.style.setProperty("--deal-y", `${Math.round(y)}px`);
    }
  }, [dealtIds]);

  const focusCardAtPointer = (clientX: number, clientY: number) => {
    if (typeof document.elementsFromPoint !== "function") return;
    const pointedCard = document.elementsFromPoint(clientX, clientY)
      .map((element) => element.closest<HTMLButtonElement>("button.card"))
      .find((card): card is HTMLButtonElement => card !== null && card.closest(".hand") !== null);
    const index = visibleCards.findIndex((card) => card.instanceId === pointedCard?.dataset.instanceId);
    if (index >= 0) setFocusedCardIndex((current) => current === index ? current : index);
  };

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>, cardInstance: CardInstance, definition: CardDefinition, playable: boolean, index: number) => {
    setFocusedCardIndex(index);
    if (!playable || selecting || event.pointerType === "mouse" && event.button !== 0) return;
    // 前回のドラッグで click が発火しなかった場合でも、次の新しい操作は通常のタップとして扱う。
    const card = event.currentTarget;
    // ポインター捕捉が使えない環境でも、ドラッグ本体は必ず開始する。
    // setPointerCapture は対応していても例外になる WebView があるため、
    // 状態を作った後に補助的に試みるだけにする。
    drag.current = {
      pointerId: event.pointerId,
      instanceId: cardInstance.instanceId,
      cardInstance,
      definition,
      card,
      startX: event.clientX,
      startY: event.clientY,
      dragging: false,
      dropZone: null,
    };
    if (typeof card.setPointerCapture === "function") {
      try {
        card.setPointerCapture(event.pointerId);
      } catch {
        // Pointer Events 実装が不完全な WebView では捕捉なしで通常どおり処理する。
      }
    }
  };

  const onPointerMove = (event: DragMoveEvent) => {
    focusCardAtPointer(event.clientX, event.clientY);
    const active = drag.current;
    if (!active || active.pointerId !== event.pointerId) return;
    const x = event.clientX - active.startX;
    const y = event.clientY - active.startY;
    if (!active.dragging && Math.hypot(x, y) < 8) return;

    cancelLongPress();
    event.preventDefault();
    if (!active.dragging) {
      active.dragging = true;
      dismissPreview();
      active.card.classList.add("is-dragging");
      document.querySelectorAll<HTMLElement>("[data-card-dropzone='play']").forEach((zone) => zone.classList.add("is-card-drop-ready"));
    }
    setDragPreview({ card: active.cardInstance, definition: active.definition, x: event.clientX, y: event.clientY });

    const nextDropZone = Array.from(document.querySelectorAll<HTMLElement>("[data-card-dropzone='play']"))
      .find((zone) => overlapsDropZone(event.clientX, event.clientY, zone.getBoundingClientRect())) ?? null;
    if (nextDropZone === active.dropZone) return;
    active.dropZone?.classList.remove("is-card-drop-target");
    nextDropZone?.classList.add("is-card-drop-target");
    active.dropZone = nextDropZone;
  };

  const onPointerUp = (event: DragEndEvent) => {
    const active = drag.current;
    if (!active || active.pointerId !== event.pointerId) {
      setFocusedCardIndex(null);
      return;
    }
    const shouldPlay = active.dragging && active.dropZone !== null;
    cancelLongPress();
    clearDrag();
    if (shouldPlay) dispatch({ type: "PLAY_CARD", instanceId: active.instanceId });
  };

  const onPointerCancel = (event: DragEndEvent) => {
    if (drag.current?.pointerId === event.pointerId) clearDrag();
    if (longPress.current?.pointerId === event.pointerId) cancelLongPress();
    setFocusedCardIndex(null);
  };

  const onLostPointerCapture = (event: PointerEvent<HTMLButtonElement>) => {
    if (drag.current?.pointerId === event.pointerId) clearDrag();
    if (longPress.current?.pointerId === event.pointerId) cancelLongPress();
  };

  useEffect(() => {
    // Pointer capture が使えない WebView では、指がカードの外へ出るとカード自身は
    // move / up を受け取れない。カード外から届いたイベントだけを補完して二重処理を避ける。
    const cameFromActiveCard = (target: EventTarget | null) => target instanceof Node && drag.current?.card.contains(target);
    const onWindowPointerMove = (event: globalThis.PointerEvent) => {
      if (!cameFromActiveCard(event.target)) onPointerMove(event);
    };
    const onWindowPointerUp = (event: globalThis.PointerEvent) => {
      if (!cameFromActiveCard(event.target)) onPointerUp(event);
    };
    const onWindowPointerCancel = (event: globalThis.PointerEvent) => {
      if (!cameFromActiveCard(event.target)) onPointerCancel(event);
    };
    window.addEventListener("pointermove", onWindowPointerMove);
    window.addEventListener("pointerup", onWindowPointerUp);
    window.addEventListener("pointercancel", onWindowPointerCancel);
    return () => {
      window.removeEventListener("pointermove", onWindowPointerMove);
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerCancel);
    };
  });

  const onLongPressStart = (event: PointerEvent<HTMLButtonElement>, card: CardInstance, definition: CardDefinition) => {
    if (event.pointerType !== "touch") return;
    cancelLongPress();
    const pointerId = event.pointerId;
    const timer = window.setTimeout(() => {
      if (longPress.current?.pointerId !== pointerId) return;
      setPreview({ card, definition, pinned: true });
      longPress.current = null;
    }, 450);
    longPress.current = { pointerId, startX: event.clientX, startY: event.clientY, timer, card, definition };
  };

  const onLongPressMove = (event: PointerEvent<HTMLButtonElement>) => {
    const active = longPress.current;
    if (!active || active.pointerId !== event.pointerId) return;
    if (Math.hypot(event.clientX - active.startX, event.clientY - active.startY) >= 8) cancelLongPress();
  };

  const onLongPressEnd = (event: PointerEvent<HTMLButtonElement>) => {
    if (longPress.current?.pointerId === event.pointerId) cancelLongPress();
  };
  return (
    <section className="hand-lane" aria-label="手札">
      <div className="hand-head">
        <div className="hand-head-label">
          <span>手札</span>
          <span className="hand-status" role="status">
            {run.activeCase?.actionDrawLimit != null && (
              <button
                type="button"
                className="hand-status-badge"
                aria-haspopup="dialog"
                aria-label={`施策ドロー上限${run.activeCase.actionDrawLimit}枚：詳細を開く`}
                onClick={() => dispatch({ type: "OPEN_OVERLAY", overlay: "deck" })}
              >
                施策ドロー上限{run.activeCase.actionDrawLimit}枚
              </button>
            )}
          </span>
        </div>
        <div className="hand-head-actions">
          <span className="stat is-energy"><StatIcon name="action" />行動 <b>{run.energy}</b></span>
        </div>
      </div>
      {selecting && pending.kind === "inn" ? (
        <p className="pending-count">捨てる手札を{innRequiredCount(run.hand.length)}枚選ぶ</p>
      ) : null}
      <div className="hand" ref={handRef}>
        {visibleCards.map((card, index) => (
          <HandCard
            key={card.instanceId}
            card={card}
            dealOrder={dealtIds.includes(card.instanceId) ? dealtIds.indexOf(card.instanceId) : null}
            dealBaseDelayMs={dealBaseDelayMs}
            run={run}
            pending={selecting ? pending : null}
            dispatch={dispatch}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onLostPointerCapture={onLostPointerCapture}
            onPreview={(event, card, definition) => {
              if (event.pointerType === "mouse" && !drag.current) setPreview({ card, definition, pinned: false });
            }}
            onPreviewEnd={() => setPreview((current) => current?.pinned ? current : null)}
            onLongPressStart={onLongPressStart}
            onLongPressMove={onLongPressMove}
            onLongPressEnd={onLongPressEnd}
            fanAngle={fanAngle(index, visibleCards.length, focusedCardIndex)}
            fanStep={steps[index]}
            fanOffset={fanFocusOffset(index, focusedCardIndex)}
            index={index}
          />
        ))}
      </div>
      {preview ? (
        <div className={`card-preview-overlay${preview.pinned ? " is-pinned" : ""}`} aria-hidden="true" onPointerDown={preview.pinned ? dismissPreview : undefined}>
          <CardView card={preview.card} definition={preview.definition} disabled className="card-preview" />
        </div>
      ) : null}
      {dragPreview ? (
        <div className="card-drag-overlay" style={{ left: dragPreview.x, top: dragPreview.y }} aria-hidden="true">
          <CardView card={dragPreview.card} definition={dragPreview.definition} disabled className="card-drag-preview" />
        </div>
      ) : null}
    </section>
  );
}
